import React from 'react';
import styled from '@emotion/styled';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  width?: number;
  height?: number;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
}

/**
 * OptimizedImage Component
 * 
 * ZENTRALE Komponente für alle optimierten Bilder auf der Website.
 * Wird von ALLEN Seiten importiert und verwendet - NICHT duplizieren!
 * 
 * Features:
 * - Responsive images with srcset
 * - WebP/AVIF format support with fallback
 * - Lazy loading by default
 * - Proper sizing to reduce download size
 * 
 * Verwendung in Seiten:
 * ```tsx
 * import OptimizedImage from '../components/OptimizedImage';
 * 
 * <OptimizedImage 
 *   src="/yoga/yoga-intro.jpg" 
 *   alt="Yoga"
 *   sizes="(max-width: 968px) 100vw, 60vw"
 *   width={1410}
 *   height={940}
 * />
 * ```
 * 
 * WICHTIG: Diese Komponente ist zentral in src/components/
 * - ❌ NICHT in einzelnen Seiten duplizieren
 * - ✅ Von allen Seiten importieren und verwenden
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className,
  style,
  width,
  height,
  sizes = '100vw',
  loading = 'lazy',
  fetchPriority = 'auto',
  objectFit = 'cover',
  objectPosition = 'center'
}) => {
  // Generate srcset for responsive images
  // Uses multiple sizes generated by optimize-images.js script
  const generateSrcSet = (format: 'original' | 'webp' | 'avif' = 'original') => {
    if (!width) return undefined;
    
    // Determine available sizes based on image width
    const availableSizes: number[] = [];
    if (width >= 2000) availableSizes.push(400, 800, 1200, 1600, 2000);
    else if (width >= 1600) availableSizes.push(400, 800, 1200, 1600);
    else if (width >= 1410) availableSizes.push(400, 800, 1200, 1410);
    else if (width >= 1200) availableSizes.push(400, 800, 1200);
    else if (width >= 800) availableSizes.push(400, 800);
    else if (width >= 400) availableSizes.push(400);
    else availableSizes.push(width);
    
    // Filter sizes that don't exceed original width
    const sizes = availableSizes.filter(s => s <= width);
    
    // Generate srcset entries for each size
    const basePath = src.replace(/\.(jpg|jpeg|png)$/i, '');
    const extension = format === 'original' 
      ? src.match(/\.(jpg|jpeg|png)$/i)?.[0] || '.jpg'
      : format === 'webp' ? '.webp' : '.avif';
    
    return sizes
      .map(size => `${basePath}-${size}w${extension} ${size}w`)
      .join(', ');
  };

  const originalSrcSet = generateSrcSet('original');
  const webpSrcSet = generateSrcSet('webp');
  const avifSrcSet = generateSrcSet('avif');

  return (
    <picture>
      {/* AVIF format - best compression */}
      {avifSrcSet && (
        <source
          srcSet={avifSrcSet}
          sizes={sizes}
          type="image/avif"
        />
      )}
      
      {/* WebP format - good compression, wider support */}
      {webpSrcSet && (
        <source
          srcSet={webpSrcSet}
          sizes={sizes}
          type="image/webp"
        />
      )}
      
      {/* Fallback to original format */}
      <StyledImg
        src={src}
        srcSet={originalSrcSet}
        sizes={sizes}
        alt={alt}
        className={className}
        style={style}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        objectFit={objectFit}
        objectPosition={objectPosition}
      />
    </picture>
  );
};

const StyledImg = styled.img<{ objectFit: string; objectPosition: string }>`
  width: 100%;
  height: auto;
  object-fit: ${props => props.objectFit};
  object-position: ${props => props.objectPosition};
  display: block;
  
  &.desktop-image {
    display: block;
  }
  
  &.mobile-image {
    display: none;
  }
  
  &.visible.desktop-image {
    display: block;
  }
  
  &.visible.mobile-image {
    display: none;
  }
  
  @media (max-width: 1100px) {
    &.desktop-image {
      display: none;
    }
    
    &.mobile-image {
      display: block;
    }
    
    &.visible.desktop-image {
      display: none;
    }
    
    &.visible.mobile-image {
      display: block;
    }
  }
`;

export default OptimizedImage;

